import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserPreferences, UserRole, UserIndustry } from '../contexts/UserPreferencesContext';
import { RoleSelector } from '../components/RoleSelector';
import { IndustrySelector } from '../components/IndustrySelector';
import { Footer } from '../components/Footer';
import { trackEmailGateEvent } from '../services/EmailGatingService';
import './LandingScreen.css';

type OnboardingStep = 'role' | 'industry';

const LandingScreen: React.FC = () => {
  const navigate = useNavigate();
  const { preferences, setRole, setIndustry, completeOnboarding } = useUserPreferences();

  // Local state for the current step
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('role');
  const [localRole, setLocalRole] = useState<UserRole>(preferences.role);
  const [localIndustry, setLocalIndustry] = useState<UserIndustry>(preferences.industry);

  const handleRoleSelect = (role: UserRole) => {
    setLocalRole(role);
  };

  const handleIndustrySelect = (industry: UserIndustry) => {
    setLocalIndustry(industry);
  };

  const handleContinue = () => {
    if (currentStep === 'role' && localRole) {
      setRole(localRole);
      setCurrentStep('industry');
    } else if (currentStep === 'industry' && localIndustry) {
      setIndustry(localIndustry);
      trackEmailGateEvent('onboarding_complete', {
        role: localRole,
        industry: localIndustry,
      });
      completeOnboarding();
      navigate('/scenarios');
    }
  };

  const handleBack = () => {
    if (currentStep === 'industry') {
      setCurrentStep('role');
    }
  };

  const handleSkip = () => {
    trackEmailGateEvent('onboarding_complete', {
      role: localRole,
      industry: localIndustry,
      skipped: true,
    });
    completeOnboarding();
    navigate('/scenarios');
  };

  const canContinue = currentStep === 'role' ? localRole !== null : localIndustry !== null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const stepVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  return (
    <div className="landing-screen min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-dark-primary dark:to-dark-secondary py-8 px-4 transition-all duration-500">
      <motion.div
        className="max-w-4xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8 md:mb-12" variants={itemVariants}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-wide mb-4 aurora-text">
            Cybersecurity Incident Simulation
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Experience realistic cybersecurity scenarios tailored to your role and industry.
            Make critical decisions under pressure and see how your choices impact the business.
          </p>
        </motion.div>

        {/* Progress indicator */}
        <motion.div className="flex justify-center mb-8" variants={itemVariants}>
          <div className="flex items-center gap-3">
            <div className={`step-indicator ${currentStep === 'role' ? 'active' : 'completed'}`}>
              {currentStep === 'industry' ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                '1'
              )}
            </div>
            <div className={`step-connector ${currentStep === 'industry' ? 'active' : ''}`} />
            <div className={`step-indicator ${currentStep === 'industry' ? 'active' : ''}`}>
              2
            </div>
          </div>
        </motion.div>

        {/* Step content */}
        <motion.div
          className="bg-white/90 dark:bg-gradient-to-br dark:from-dark-secondary dark:to-dark-accent border border-slate-200 dark:border-aurora-violet-500/30 rounded-2xl p-6 md:p-8 backdrop-blur-md shadow-xl dark:shadow-aurora-card"
          variants={itemVariants}
        >
          <AnimatePresence mode="wait">
            {currentStep === 'role' && (
              <motion.div
                key="role-step"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-6">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                    What best describes your role?
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
                    We'll personalize scenarios to match your perspective and decision-making context.
                  </p>
                </div>

                <RoleSelector selectedRole={localRole} onSelect={handleRoleSelect} />
              </motion.div>
            )}

            {currentStep === 'industry' && (
              <motion.div
                key="industry-step"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-6">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                    What industry are you in?
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
                    Experience scenarios with industry-specific regulations, threats, and business contexts.
                  </p>
                </div>

                <IndustrySelector selectedIndustry={localIndustry} onSelect={handleIndustrySelect} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Navigation buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-between mt-8 gap-4"
          variants={itemVariants}
        >
          <div className="order-2 sm:order-1">
            {currentStep === 'industry' ? (
              <button
                onClick={handleBack}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <button
                onClick={handleSkip}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Skip to all scenarios
              </button>
            )}
          </div>

          <motion.button
            onClick={handleContinue}
            disabled={!canContinue}
            className={`
              order-1 sm:order-2 w-full sm:w-auto px-8 py-3 rounded-xl font-medium
              transition-all duration-300
              ${canContinue
                ? 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 dark:from-aurora-violet-600 dark:to-aurora-violet-700 text-white hover:shadow-lg dark:hover:shadow-aurora-violet hover:-translate-y-0.5'
                : 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
              }
            `}
            whileHover={canContinue ? { scale: 1.02 } : {}}
            whileTap={canContinue ? { scale: 0.98 } : {}}
          >
            {currentStep === 'industry' ? 'See Recommendations' : 'Continue'}
            <svg className="inline-block w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </motion.div>

        {/* Footer */}
        <motion.div variants={itemVariants}>
          <Footer className="mt-8" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LandingScreen;
